/**
 * Records every PostHog flag key the app queries via
 * `posthog.isFeatureEnabled` / `posthog.getFeatureFlag`, so the dev
 * panel can display override rows for flags PostHog hasn't evaluated
 * for the current user.
 *
 * Why we need this: PostHog's `featureFlags.getFlags()` only returns
 * flags it has resolved for the current user (subject to release
 * conditions, cohort targeting, region filters, etc.). A flag the app
 * reads but the user is excluded from never shows up in `getFlags()`,
 * so the helper can't render a row for it — making "Set all on" miss
 * exactly the flags the developer most wants to test.
 *
 * Call `initPostHogCallTracker()` once after PostHog has been
 * initialized. It's a no-op outside of `window` (e.g. SSR), idempotent,
 * and composes safely with the `NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS`
 * monkey-patch in `flags.ts` (whoever wraps last sees the call first
 * and forwards to the next implementation).
 *
 * Production safety: callers must gate on
 * `getEnvironment() !== ENV_NAME.PROD`. The wrapper itself doesn't
 * check the env so it can be unit-tested cheaply.
 */
import posthog from 'posthog-js';

import { recordPostHogKey } from '@/features/flags-dev/discovery';

let installed = false;

export const initPostHogCallTracker = (): void => {
  if (typeof window === 'undefined') return;
  if (installed) return;

  const ph = posthog as typeof posthog & {
    isFeatureEnabled: (...args: unknown[]) => boolean | undefined;
    getFeatureFlag: (...args: unknown[]) => string | boolean | undefined;
  };

  const originalIsEnabled = ph.isFeatureEnabled.bind(ph);
  const originalGetFlag = ph.getFeatureFlag.bind(ph);

  ph.isFeatureEnabled = (...args: unknown[]) => {
    const key = args[0];
    if (typeof key === 'string') recordPostHogKey(key);
    return originalIsEnabled(...args);
  };
  ph.getFeatureFlag = (...args: unknown[]) => {
    const key = args[0];
    if (typeof key === 'string') recordPostHogKey(key);
    return originalGetFlag(...args);
  };

  installed = true;
};
